import { cn } from '@costar-ui/cosmos'
import { IconArrowDefaultDown } from '@costar-ui/icons/csg/IconArrowDefaultDown'
import { IconArrowDefaultUp } from '@costar-ui/icons/csg/IconArrowDefaultUp'
import { isBrowser, reactNodeToString } from '@costar-ui/utils'
import { flexRender } from '@tanstack/react-table'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import type {
  CSSProperties,
  MouseEvent,
  ReactNode,
  SyntheticEvent
} from 'react'

import evaluateCellScrollWidth from './evaluate-cell-scroll-width'
import HeadCell from './primitives/head-cell'
import { useTableContext } from './primitives/table-context-provider'
import type { ColumnMeta, TableHeaderProps } from './types'

function TableHeaderCell<TData, TValue = ReactNode>(
  props: TableHeaderProps<TData, TValue>
) {
  const { className, header, ...rest } = props
  const meta: ColumnMeta = header.column.columnDef.meta || {}
  const isSticky = meta.className?.includes('sticky')
  const isLastStickyCell = meta.className?.includes(
    'csg-tw-table-cell-fix-left-last'
  )
  const fixLeft = isSticky ? header.column.getStart() : 0
  const width = header.getSize()
  const { fillContainer } = useTableContext()
  const isFillContainerDummyColumn =
    header.id === 'fillContainerColumn' && fillContainer === 'append'
  const ref = useRef<HTMLTableCellElement>(null)
  const [showTitle, setShowTitle] = useState(false)
  const [isHeaderHovered, setIsHeaderHovered] = useState(false)
  const headerText = useMemo(
    () =>
      typeof header.column.columnDef.header === 'function'
        ? reactNodeToString(header.column.columnDef.header(header.getContext()))
        : '',
    [header]
  )
  const handleHeaderCellMouseEnter = useCallback(
    (e: SyntheticEvent<HTMLTableCellElement>) => {
      e.stopPropagation()
      setIsHeaderHovered(true)
    },
    []
  )

  const handleHeaderCellMouseLeave = useCallback(
    (e: SyntheticEvent<HTMLTableCellElement>) => {
      e.stopPropagation()
      setIsHeaderHovered(false)
    },
    []
  )
  const handleOnMouseOver = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      // Check if the source of the event is the header cell or a element contained within it
      if (
        ref.current &&
        (e.currentTarget === ref.current ||
          ref.current.contains(e.target as Node))
      ) {
        const widthOffset = isLastStickyCell ? 15 : 0 // left fixed columns need a bigger scroll offset for elipsis handling.
        const compare = evaluateCellScrollWidth(ref.current, widthOffset)
        setShowTitle(compare)
      }
    },
    [ref, isLastStickyCell] // Dependency on ref
  )

  const headerStyle: CSSProperties = useMemo(
    () =>
      isFillContainerDummyColumn
        ? {}
        : {
            width,
            minWidth: width,
            ...(fixLeft > 0 ? { left: fixLeft } : {})
          },
    [isFillContainerDummyColumn, width, fixLeft]
  )
  /*
  if (isSticky) {
    console.log(
      `${
        header.column.id
      }-  offset:${header.column.getStart()}, size: ${header.column.getSize()}`
    )
  }
  */
  const title = useMemo(() => {
    return showTitle ? headerText : ''
  }, [showTitle, headerText])

  // const isNonInteractiveNode =
  //   isBrowser &&
  //   (ref.current?.firstChild?.nodeType === Node.TEXT_NODE || // Text node
  //     (ref.current?.firstChild instanceof HTMLElement &&
  //       ref.current?.firstChild.tagName !== 'BUTTON')) // Non-button element

  const isNonInteractiveNode = () => {
    if (isBrowser && ref?.current?.firstChild instanceof HTMLElement) {
      const element = ref.current.firstChild

      return element.tagName === 'SPAN' || element.nodeType === Node.TEXT_NODE
    }
    return false
  }

  const isSortable = header.column.getCanSort() && !header.isPlaceholder

  const sortingIcon =
    (isSortable &&
      {
        asc: <IconArrowDefaultUp className="shrink-0" size="1.5em" />,
        desc: <IconArrowDefaultDown className="shrink-0" size="1.5em" />
      }[header.column.getIsSorted() as string]) ??
    null

  const sortableHeaderCell = useMemo(
    () => (
      <button
        className={cn(
          'm-0 flex cursor-pointer select-none items-center justify-between gap-2 border border-transparent bg-transparent p-0 text-current',
          'transition-shadow duration-300 ease-in-out',
          'hover:opacity-60'
        )}
        onMouseOver={handleOnMouseOver}
        onClick={
          isSortable ? header.column.getToggleSortingHandler() : undefined
        }
        // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
        style={{ maxWidth: width - 20 }}
      >
        <span className="text-sm block truncate">
          {flexRender(header.column.columnDef.header, header.getContext())}
        </span>
        {sortingIcon}
      </button>
    ),
    [handleOnMouseOver, header, isSortable, sortingIcon, width]
  )
  // eslint-disable-next-line no-console
  // console.log(`showTitle: ${showTitle} title: ${title}`)
  return (
    <HeadCell
      ref={ref}
      className={cn(
        className,
        {
          // This will allow the onMouseOver to work when the cell text is wrap in another element.
          '[&>*:first-child]:pointer-events-none': isNonInteractiveNode(),
          'z-20': isSticky
        },
        meta.className,
        meta.headerClassName
      )}
      title={title}
      onMouseEnter={handleHeaderCellMouseEnter}
      onMouseLeave={handleHeaderCellMouseLeave}
      onMouseOver={handleOnMouseOver}
      colSpan={header.colSpan}
      style={headerStyle}
      {...rest}
    >
      {isSortable
        ? sortableHeaderCell
        : header.isPlaceholder
          ? null // do not show any elements if placeholder cell
          : flexRender(header.column.columnDef.header, header.getContext())}
      {header.column.getCanResize() && (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={`csg-tw-table-column-resizer ${
            header.column.getIsResizing() ? 'isResizing' : ''
          }
          ${!header.isPlaceholder && isHeaderHovered ? 'opacity-60' : ''}
          `}
        />
      )}
    </HeadCell>
  )
}

export default TableHeaderCell
